import {  Box, Grid, TextField, Typography, useMediaQuery  } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { Icon } from "../../components/Icon/Icon.component";
import {  FormValues, Errors, MultiSelectOptionsProps  } from "../../routes/Profile/AddSkillBook/AddSkillBook.types";
import InputGroupColumn from "../InputCst/InputGroupColumn";
import MultiSelectCst from "../InputCst/MultiSelectCst";
import SelectCst from "../InputCst/SelectCst";
import { Options } from "components/InputCst/MultiSelectCst/types";


interface ObiettiviFormativiSectionProps {
  expanded: boolean;
  onToggle: () => void;
  formValues: FormValues;
  setFormValues: React.Dispatch<React.SetStateAction<FormValues>>;
  errors: Errors;
  t: (key: string) => string;
  knowledgeAreas: MultiSelectOptionsProps[];
  knowledgeDomains: MultiSelectOptionsProps[];
  capabilities: MultiSelectOptionsProps[];
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKnowledgeAreaChange: (value: string | number) => void;
  handleMultiSelectChange: (
    event: {  label: string; value: string | number  }[],
    accessorKey: string
  ) => void;
  readOnly?: boolean;
  campaignOptions: Options[];
 }

const ObiettiviFormativiSection: React.FC<ObiettiviFormativiSectionProps> = ({
  expanded,
  onToggle,
  formValues,
  setFormValues,
  errors,
  t,
  knowledgeAreas,
  knowledgeDomains,
  capabilities,
  handleInputChange,
  onKnowledgeAreaChange,
  handleMultiSelectChange,
  readOnly,
  campaignOptions
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isCustomSize = useMediaQuery("(max-width: 1350px)");




  return (
    <Box
      sx={ {
        background: theme.customColors.backgroundPrimary,
        borderRadius: "12px",
        mb: 2,
        border: `${theme.customColors.borderAccent} 1px solid`,
        p: 2,
        boxShadow: `${theme.customColors.borderAccent} 0 0 10px`,
        maxHeight: expanded ? undefined : "65px",
        gap: isMobile ? 1 : 0,
        flexDirection: isCustomSize ? "column" : "row"
      } }
    >
      <Box
        onClick={ onToggle }
        aria-pressed={ expanded }
        sx={ {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          mb: 2
        } }
      >
        <Typography sx={ {  fontWeight: "bold", color: theme.customColors.textPrimary, fontSize: "1.2rem"  } }>
          { t("training_objectives") }
        </Typography>
        <Icon
          icon="dropdown"
          color={ theme.customColors.systemPrimary02 }
          width="24px"
          height="19px"
          style={ {
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform .4s linear",
            color: theme.customColors.borderAccent
          } }
        />
      </Box>

      { expanded && (
        <Grid container spacing={ 2 }>
          <Grid item xs={ 12 }>
            <p style={ {  fontSize: "10px"  } }>{ t("motivation_field") }</p>
            <TextField
              fullWidth
              multiline
              name="motivation"
              label="-"
              value={ formValues.motivation || "" }
              onChange={ handleInputChange }
              error={ !!errors.motivation }
              helperText={ errors.motivation }
              sx={ {
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: theme.customColors.textDisabledAlternative, // testo disabilitato
                  opacity: 1
                },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.customColors.backgroundPrimary,
                  color: theme.customColors.textPrimary,
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: theme.customColors.border
                  }
                },
                "& .MuiInputLabel-root": {
                  color: theme.customColors.textPrimary,
                  backgroundColor: theme.customColors.backgroundPrimary,
                  borderRadius: "8px",
                  paddingX: 0.5
                }
              } }
              InputLabelProps={ {  style: {  color: "#ccc"  }  } }
              disabled={ readOnly }
            />
          </Grid>

          <Grid item xs={ 12 }>
            { /* Label esterna personalizzata sopra il gruppo Area/Dominio */ }
            <Typography sx={ { fontSize: "10px", color: theme.customColors.textPrimary, mb: 1 } }>
              { t("area_domain_field") }
            </Typography>

            <Box sx={ { maxWidth: 400, overflow: "hidden" } }>
              <InputGroupColumn
                fields={ [
                  {
                    label: "Area di conoscenza",
                    accessorKey: "knowledge_area",
                    type: "select",
                    labelHeader: {
                      it: "Area di conoscenza",
                      en: "Knowledge Area"
                    },
                    options: knowledgeAreas,
                    required: false
                  },
                  {
                    label: "Dominio di conoscenza",
                    accessorKey: "knowledge_domain",
                    type: "select",
                    labelHeader: {
                      it: "Dominio di conoscenza",
                      en: "Knowledge Domain"
                    },
                    options: knowledgeDomains,
                    dependentOn: "knowledge_area",
                    required: false
                  }
                ] }
                disabled={ readOnly }
                onChange={ (newValue) => setFormValues(prev => ({ ...prev, area_domain: newValue })) }
                value={ formValues.area_domain || [] }
                onFieldChange={ (value) => onKnowledgeAreaChange(value) }
                readOnly={ readOnly }
              />
            </Box>
          </Grid>

          <Grid item xs={ 12 }>
            <p style={ {  fontSize: "10px"  } }>{ t("capability_field") }</p>
            <MultiSelectCst
              options={ capabilities }
              selectedOptions={ formValues.capability as MultiSelectOptionsProps[] || [] }
              handleChange={ (e) => handleMultiSelectChange(e, "capability") }
              label="-"
              disabled={ readOnly }
            />
          </Grid>

          <Grid item xs={ 12 }>
            <p style={ { fontSize: "10px" } }>{ t("campaign_field") }</p>
            <SelectCst
              options={ [{ label: "-", value: " " }, ...campaignOptions] }
              name="campaign"
              value={ typeof formValues.campaignId === "string"
                ? formValues.campaignId
                : "" }
              onChange={ (e) =>
                setFormValues(prev => ({ ...prev, campaignId: e.target.value }))
              }
              readOnly={ readOnly }
              error={ !!errors.campaignId }
              errorMessage={ errors.campaignId }
            />
          </Grid>

        </Grid>
      ) }
    </Box>
  );
};

export default ObiettiviFormativiSection;
